<script setup lang="ts">
import CommonBtn from '@/components/common-btn/CommonBtn.vue'
import FooterBar from '@/components/footer-bar/FooterBar.vue'
import { ref, watch } from 'vue'
import {
  RuneRoleText,
  batchSellList,
  batchSellRuneList,
  equipIcon,
  equipQualityList,
  equipQualityText,
  equipTypeList,
  relicIcon,
  itemPageTypeMap,
} from './data'
import type { EquipItemType, EquipQuality, EquipSellRes, EquipType } from '@/types/equip'
import { equipCollection } from '@/core/equip'
import { computed } from 'vue'
import { useUserStore } from '@/stores/modules/user'
import EquipInfo from './components/EquipInfo.vue'
import EquipCompare from './components/EquipCompare.vue'
import { socketEmitAsync, socketOn } from '@/utils/web-socket'
import type { ItemShowType, ItemType } from '@/types/item'
import UserAttr from '@/components/user-attr/UserAttr.vue'
import { Lock } from '@element-plus/icons-vue'
import EquipRoutine from './components/EquipRoutine.vue'
import { getEquipName } from '@/utils/equip'
import { sortItemList, sortRuneItem, sortRelicItem, sortSkillItem } from '@/utils/item'
import { getRuneName, runeCollection } from '@/core/rune'
import type { RuneItemType, RuneSellRes } from '@/types/rune'
import RuneInfo from './components/RuneInfo.vue'
import RuneCompare from './components/RuneCompare.vue'
import { soulClass } from '../awaken/data'
import { useCommonStore } from '@/stores/modules/common'
import type { UserAttrType } from '@/types/user'
import { equipAffixCollection } from '@/core/equip/affix'
import type { RelicItemType } from '@/types/relic'
import { getRelicName, relicCollection } from '@/core/relic'
import RelicInfo from './components/RelicInfo.vue'
import RelicCompare from './components/RelicCompare.vue'
import { getSkillName, skillCollection } from '@/core/skill'
import type { SkillItemType } from '@/types/skill'
import SkillInfo from './components/SkillInfo.vue'
import SkillCompare from './components/SkillCompare.vue'
import { skillItemElementIcon } from '@/core/skill'

const commonStore = useCommonStore()
const userStore = useUserStore()

const equipInfo = ref<EquipItemType>()
const isInItems = ref(false)
function showItemDetail(item: EquipItemType | undefined, openType: boolean) {
  equipInfo.value = item
  isInItems.value = openType
}

const equippedList = computed(() =>
  equipTypeList.value.map((item) => {
    const itemId = userStore.equippedList[item]
    const equipInfo = userStore.itemList.find((item) => item.id === itemId)
    if (!equipInfo) return undefined
    const { name, type, attrs } = equipCollection[equipInfo.equipId]
    return { ...equipInfo, name, type, attrs }
  }),
)

const itemList = computed<EquipItemType[]>(() => {
  const items = userStore.itemList
  if (userStore.itemList.length === 0) return []
  const curItemList = items
    .filter((item) => {
      const equippedIdList = Object.values(userStore.equippedList)
      return !equippedIdList.includes(item.id)
    })
    .map((item) => {
      const { equipId } = item
      const { name, type, attrs } = equipCollection[equipId]
      return { ...item, name, type, attrs }
    })
  return curItemList.filter((item) => {
    const specialNameList = item.attrs.special?.map((item) => item.name)
    const suitAffixNameList = item.attrs.suit?.attrs.affix?.map((item) => item.name)
    const darkGoldAffixNameList = item.darkGoldAttrs?.special.map((item) => equipAffixCollection[item.key].name)
    const engraveAffixNameList = item.engraveAttr?.special.map((item) => equipAffixCollection[item.key].name)
    return (
      (filterType.value.length ? filterType.value.includes(item.type) : true) &&
      (filterQuality.value.length ? filterQuality.value.includes(item.quality) : true) &&
      item.name.includes(filterName.value) &&
      (filterAffix.value
        ? suitAffixNameList?.includes(filterAffix.value) ||
          specialNameList?.includes(filterAffix.value) ||
          darkGoldAffixNameList?.includes(filterAffix.value) ||
          engraveAffixNameList?.includes(filterAffix.value)
        : true)
    )
  })
})
const itemListTotal = computed(() => {
  return userStore.itemList.filter((item) => {
    const equippedIdList = Object.values(userStore.equippedList)
    return !equippedIdList.includes(item?.id)
  }).length
})

async function handleArrange() {
  await socketEmitAsync<ItemType[]>({ type: 'arrangeEquip' })
  const res = sortItemList(userStore.itemList)
  userStore.itemList = res
}

const filterType = ref<EquipType[]>([])
const filterQuality = ref<EquipQuality[]>([])
const filterName = ref('')
const filterAffix = ref('')
function handleAddFilterType(type: EquipType) {
  const index = filterType.value.findIndex((item) => item === type)
  if (index !== -1) return filterType.value.splice(index, 1)
  filterType.value.push(type)
}
function handleAddFilterQuality(quality: EquipQuality) {
  const index = filterQuality.value.findIndex((item) => item === quality)
  if (index !== -1) return filterQuality.value.splice(index, 1)
  filterQuality.value.push(quality)
}
function handleResetFilter() {
  filterType.value = []
  filterQuality.value = []
  filterName.value = ''
  filterAffix.value = ''
}

const getBatSellSliceArr = computed(() => {
  const isAdvance = userStore.advanceLevel === 4
  const isPeak = userStore.soulType > 3
  if (isPeak) {
    return [5]
  } else if (isAdvance) {
    return [2, 6]
  } else {
    return [0, 6]
  }
})
function handleBatchSell({ name, key }: { name: string; key: string }) {
  let filterQuality: string[] = []
  if (key === 'normal') {
    filterQuality = ['normal']
  } else if (key === 'race') {
    filterQuality = ['normal', 'race']
  } else if (key === 'epic') {
    filterQuality = ['normal', 'race', 'epic']
  } else if (key === 'unique') {
    filterQuality = ['normal', 'race', 'epic', 'unique']
  } else if (key === 'legend') {
    filterQuality = ['normal', 'race', 'epic', 'unique', 'legend']
  } else if (key === 'peerless') {
    filterQuality = ['normal', 'race', 'epic', 'unique', 'legend', 'antiquity', 'peerless']
  } else if (key === 'darkGold') {
    filterQuality = ['normal', 'race', 'epic', 'unique', 'legend', 'antiquity', 'peerless', 'darkGold']
  }
  ElMessageBox.confirm(`<p class="affix">确定出售<span class="${key}">${name}及以下</span>品质的装备吗？（已锁定、已打造的装备除外）</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const equippedIdList = Object.values(userStore.equippedList)
      const equipList = userStore.itemList.filter(
        (item) =>
          filterQuality.includes(item.quality) &&
          !equippedIdList.includes(item.id) &&
          !item.isLock &&
          !item.reinforcedLevel &&
          !item.starAttrs &&
          !item.enchantAttr &&
          !item.refineAttr &&
          !item.engraveAttr &&
          !item.supremeEngraveAttr,
      )
      sellEquip(equipList.map((item) => item.id))
      equipInfo.value = undefined
    })
    .catch(() => {})
}
function handleSellAll() {
  ElMessageBox.confirm(`<p class="affix">确定出售<span class="hp">所有</span>的装备吗？（已锁定、已打造的装备除外）</p>`, '提示', {
    type: 'warning',
    dangerouslyUseHTMLString: true,
    appendTo: 'body',
    confirmButtonClass: 'is-plain',
  })
    .then(async () => {
      const equippedIdList = Object.values(userStore.equippedList)
      const filterItemList = userStore.itemList.filter(
        (item) =>
          !equippedIdList.includes(item.id) &&
          !item.isLock &&
          !item.reinforcedLevel &&
          !item.starAttrs &&
          !item.enchantAttr &&
          !item.refineAttr &&
          !item.engraveAttr &&
          !item.supremeEngraveAttr,
      )
      const sellEquipIdList = filterItemList.map((item) => item.id)
      for (const item of filterItemList) {
        const isAdvance = userStore.advanceLevel === 4
        let qualityTipMsg = ''
        if (!isAdvance && (item.quality === 'antiquity' || item.quality === 'peerless')) {
          qualityTipMsg = '<span class="antiquity">远古</span> / <span class="peerless">绝世</span>'
        } else if (isAdvance && item.quality === 'myth') {
          qualityTipMsg = '<span class="myth">神话暗金</span>'
        } else if (item.supremeAttrs?.level === 5) {
          qualityTipMsg = '<span class="supreme">S级至臻</span>'
        }
        if (!qualityTipMsg) continue
        return ElMessageBox.confirm(
          `<p class="affix">出售装备中包含${qualityTipMsg}品质装备，请再次确认，该操作<span class="hp">无法撤销</span></p>`,
          '提示',
          {
            type: 'warning',
            dangerouslyUseHTMLString: true,
            appendTo: 'body',
            confirmButtonClass: 'is-plain',
          },
        )
          .then(() => {
            sellEquip(sellEquipIdList)
            equipInfo.value = undefined
          })
          .catch(() => {})
      }

      sellEquip(sellEquipIdList)
    })
    .catch(() => {})
}
async function sellEquip(idList: string[]) {
  const { itemList, gold } = await socketEmitAsync<EquipSellRes>({ type: 'sellEquip', data: { idList } })
  userStore.itemList = itemList
  userStore.gold = gold
}

const compareShow = ref(false)
function handleCompareClose() {
  compareShow.value = false
  equipInfo.value = undefined
}

const showType = ref<ItemShowType>('equip')
const runeInfo = ref<RuneItemType>()
function showRuneDetail(rune: RuneItemType | undefined, openType: boolean) {
  runeInfo.value = rune
  isInItems.value = openType
}
function handleChangeItemType(type: ItemShowType) {
  equipInfo.value = undefined
  runeInfo.value = undefined
  relicInfo.value = undefined
  skillInfo.value = undefined
  if (type === 'rune' && userStore.soulType === 0) return ElMessage.warning('觉醒后开启符石背包')
  if (type === 'relic' && userStore.advanceLevel !== 4) return ElMessage.warning('进阶后开启圣物背包')
  showType.value = type
}

const filterSoulType = ref<number[]>([])
const filterRuneName = ref('')
function handleAddFilterSoulType(soulType: number) {
  const index = filterSoulType.value.findIndex((item) => item === soulType)
  if (index !== -1) return filterSoulType.value.splice(index, 1)
  filterSoulType.value.push(soulType)
}
function handleResetRuneFilter() {
  filterSoulType.value = []
  filterRuneName.value = ''
}
const runeItemList = computed(() =>
  userStore.runeList.filter((item) => {
    if (userStore.runeEquippedList.includes(item.id)) return false
    const { name, soulType } = runeCollection[item.runeId]
    return (filterSoulType.value.length ? filterSoulType.value.includes(soulType) : true) && name.includes(filterRuneName.value)
  }),
)
const runeEquippedList = computed(() =>
  userStore.runeEquippedList.map((item) => {
    const rune = userStore.runeList.find((rune) => rune.id === item)
    if (!rune) return undefined
    return rune
  }),
)
async function handleArrangeRune() {
  await socketEmitAsync({ type: 'arrangeRune' })
  sortRuneItem(userStore.runeList, userStore.soulType)
  ElMessage.success('整理成功')
}
function handleBatchSellRune({ name, key }: { name: string; key: string }) {
  let filterQuality: string[] = []
  if (key === 'normal') {
    filterQuality = ['normal']
  } else if (key === 'race') {
    filterQuality = ['normal', 'race']
  } else if (key === 'epic') {
    if (userStore.runeList.filter((item) => item.quality === 'legend').length < 10)
      return ElMessage.warning('需拥有超 10块传说符石才可批量出售史诗品质符石')
    filterQuality = ['normal', 'race', 'epic']
  }
  ElMessageBox.confirm(
    `<p class="affix">确定出售<span class="${key}">${name}及以下</span>品质的符石吗？（<b style="color: red">符石珍贵，慎重操作</b>）</p>`,
    '提示',
    {
      type: 'warning',
      dangerouslyUseHTMLString: true,
      appendTo: 'body',
      confirmButtonClass: 'is-plain',
    },
  )
    .then(() => {
      const runeIdList = Object.values(userStore.runeEquippedList)
      const runeList = userStore.runeList.filter((item) => filterQuality.includes(item.quality) && !runeIdList.includes(item.id) && !item.isLock)
      const idList = runeList.map((item) => item.id)
      sellRune(idList)
    })
    .catch(() => {})
}
async function sellRune(idList: string[]) {
  const { runeList, gold } = await socketEmitAsync<RuneSellRes>({ type: 'sellRune', data: { idList } })
  userStore.runeList = runeList
  userStore.gold = gold
  runeInfo.value = undefined
  ElMessage.success('出售成功')
}

const runeCompareShow = ref(false)
const runeCompareIndex = ref(-1)
function handleRuneCompare(index: number) {
  runeCompareShow.value = true
  runeCompareIndex.value = index
}
function handleCompareRuneClose() {
  compareShow.value = false
  runeInfo.value = undefined
}

const filterElementType = ref<string[]>([])
const filterRelicName = ref('')
function handleAddFilterElement(element: string) {
  const index = filterElementType.value.findIndex((item) => item === element)
  if (index !== -1) return filterElementType.value.splice(index, 1)
  filterElementType.value.push(element)
}
function handleResetRelicFilter() {
  filterElementType.value = []
  filterRelicName.value = ''
}

const relicItemList = computed(() =>
  userStore.relicList.filter((item) => {
    if (userStore.relicEquippedList.includes(item.id)) return false
    const { name, element } = relicCollection[item.relicId]
    return (filterElementType.value.length ? filterElementType.value.includes(element) : true) && name.includes(filterRelicName.value)
  }),
)
const relicEquippedList = computed(() =>
  userStore.relicEquippedList.map((item) => {
    const relic = userStore.relicList.find((relic) => relic.id === item)
    if (!relic) return undefined
    return relic
  }),
)
const relicInfo = ref<RelicItemType>()
function showRelicDetail(relic: RelicItemType | undefined, openType: boolean) {
  relicInfo.value = relic
  isInItems.value = openType
}
async function handleArrangeRelic() {
  await socketEmitAsync({ type: 'arrangeRelic' })
  sortRelicItem(userStore.relicList)
  ElMessage.success('整理成功')
}

const relicCompareShow = ref(false)
const relicCompareIndex = ref(-1)
function handleRelicCompare(index: number) {
  relicCompareShow.value = true
  relicCompareIndex.value = index
}
function handleCompareRelicClose() {
  compareShow.value = false
  relicInfo.value = undefined
}

const skillItemList = computed(() => {
  return userStore.skillList.filter((item) => {
    if (userStore.skillEquippedList.includes(item.id)) return false
    const element = skillCollection[item.skillId].element
    if (filterSkillMainElmentType.value.length) {
      if (!filterSkillMainElmentType.value.includes(element[0])) return false
    }
    if (filterSkillElementType.value.length) {
      if (!element.some((el) => filterSkillElementType.value.includes(el))) return false
    }
    if (filterSkillName.value) {
      const skillName = skillCollection[item.skillId].name
      if (!skillName.includes(filterSkillName.value)) return false
    }
    return true
  })
})
const skillEquippedList = computed(() =>
  userStore.skillEquippedList.map((item) => {
    const skill = userStore.skillList.find((skill) => skill.id === item)
    if (!skill) return undefined
    return skill
  }),
)
const skillInfo = ref<SkillItemType>()
function showSkillDetail(skill: SkillItemType | undefined, openType: boolean) {
  skillInfo.value = skill
  isInItems.value = openType
}
async function handleArrangeSkill() {
  await socketEmitAsync({ type: 'arrangeSkill' })
  sortSkillItem(userStore.skillList)
  ElMessage.success('整理成功')
}

const skillCompareShow = ref(false)
const skillCompareIndex = ref(-1)
function handleSkillCompare(index: number) {
  skillCompareShow.value = true
  skillCompareIndex.value = index
}
function handleCompareSkillClose() {
  compareShow.value = false
  skillInfo.value = undefined
}

const filterSkillMainElmentType = ref<string[]>([])
const filterSkillElementType = ref<string[]>([])
const filterSkillName = ref('')
function handleAddFilterSkillMainElement(element: string) {
  const index = filterSkillMainElmentType.value.findIndex((item) => item === element)
  if (index !== -1) return filterSkillMainElmentType.value.splice(index, 1)
  filterSkillMainElmentType.value.push(element)
}
function handleAddFilterSkillElement(element: string) {
  const index = filterSkillElementType.value.findIndex((item) => item === element)
  if (index !== -1) return filterSkillElementType.value.splice(index, 1)
  filterSkillElementType.value.push(element)
}
function handleResetSkillFilter() {
  filterSkillElementType.value = []
  filterSkillName.value = ''
}

watch(
  () => commonStore.isSocketInit,
  (val) => {
    if (!val) return
    socketOn({
      type: 'changeEquipRoutine',
      callBack: (res: { userAttr: UserAttrType; routeId: number }) => {
        const { userAttr, routeId } = res
        userStore.handleChangeEquipRoutine(userAttr, routeId)
      },
    })
  },
)
</script>

<template>
  <div class="item-page page">
    <div class="basic-info">
      <div class="user-info-wrap">
        <div class="equip-list" :style="showType !== 'equip' || userStore.soulType <= 3 ? 'flex-shrink: 0' : ''">
          <div style="padding: 0 8px">
            <EquipRoutine />
          </div>
          <div class="btn-wrap item-btn-wrap" v-show="showType === 'equip'">
            <CommonBtn
              v-for="(item, index) of equippedList"
              :key="index"
              size="small"
              @done="showItemDetail(item, false)"
              :style="{ borderColor: item?.id && equipInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" v-html="getEquipName(item, index)"></span>
              <span v-else>{{ equipIcon[equipTypeList[index]] }} (未装备)</span>
            </CommonBtn>
          </div>
          <div class="btn-wrap item-btn-wrap" v-show="showType === 'rune'">
            <CommonBtn
              v-for="(item, index) of runeEquippedList"
              :key="index"
              size="small"
              @done="showRuneDetail(item, false)"
              :style="{ borderColor: item?.id && runeInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" :class="item.quality" v-html="getRuneName(item.runeId)"></span>
              <span v-else>(未携带)</span>
            </CommonBtn>
          </div>
          <div class="btn-wrap item-btn-wrap" v-show="showType === 'relic'">
            <CommonBtn
              v-for="(item, index) of relicEquippedList"
              :key="index"
              size="small"
              @done="showRelicDetail(item, false)"
              :style="{ borderColor: item?.id && relicInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" :class="item.quality" v-html="getRelicName(item.relicId, item.count)"></span>
              <span v-else>(未携带)</span>
            </CommonBtn>
          </div>
          <div class="btn-wrap item-btn-wrap" v-show="showType === 'skill'">
            <CommonBtn
              v-for="(item, index) of skillEquippedList"
              :key="index"
              size="small"
              @done="showSkillDetail(item, false)"
              :style="{ borderColor: item?.id && skillInfo?.id === item.id ? 'red' : '' }"
              :type="item ? 'primary' : 'info'"
            >
              <span v-if="item" :class="item.quality" v-html="getSkillName(item)"></span>
              <span v-else>(未携带)</span>
            </CommonBtn>
          </div>
        </div>

        <UserAttr :show-type="showType" v-if="userStore.soulType <= 3 || showType !== 'equip'" />
        <el-button
          type="primary"
          plain
          size="small"
          style="width: 100%"
          :disabled="!equipInfo"
          v-show="showType === 'equip'"
          v-if="userStore.soulType > 3"
          @click="equipInfo = undefined"
          >查看属性 →</el-button
        >
      </div>
      <UserAttr
        v-if="userStore.soulType > 3"
        v-show="showType === 'equip' && !equipInfo"
        style="width: calc(50% - 5px); margin-left: 10px; height: 100%; max-height: unset; padding-bottom: 10px"
      />
      <div class="item-info-wrap" v-show="showType === 'equip' && userStore.soulType > 3 && equipInfo">
        <h4>{{ itemPageTypeMap[showType] }}信息</h4>
        <template v-if="equipInfo">
          <EquipInfo
            :equipInfo="equipInfo"
            :isInItems="isInItems"
            @clear="equipInfo = undefined"
            @compare="compareShow = true"
            @sell="equipInfo = undefined"
            @lock="equipInfo.isLock = !equipInfo.isLock"
          />
        </template>
      </div>
      <div class="item-info-wrap" v-show="showType !== 'equip' || userStore.soulType <= 3">
        <h4>{{ itemPageTypeMap[showType] }}信息</h4>
        <template v-if="equipInfo && userStore.soulType <= 3">
          <EquipInfo
            :equipInfo="equipInfo"
            :isInItems="isInItems"
            @clear="equipInfo = undefined"
            @compare="compareShow = true"
            @sell="equipInfo = undefined"
            @lock="equipInfo.isLock = !equipInfo.isLock"
          />
        </template>
        <template v-if="runeInfo">
          <RuneInfo :rune-info="runeInfo" :isInItems="isInItems" @clear="runeInfo = undefined" @compare="handleRuneCompare" />
        </template>
        <template v-if="relicInfo">
          <RelicInfo :relic-info="relicInfo" :isInItems="isInItems" @clear="relicInfo = undefined" @compare="handleRelicCompare" />
        </template>
        <template v-if="skillInfo">
          <SkillInfo :skill-info="skillInfo" :isInItems="isInItems" @clear="skillInfo = undefined" @compare="handleSkillCompare" />
        </template>
      </div>
    </div>

    <div class="item-list">
      <h4>
        <span>背包：</span>
        <a href="javascript:;" :style="{ opacity: showType === 'equip' ? '1' : '0.5' }" @click="handleChangeItemType('equip')">装备</a>
        <template v-if="userStore.soulType > 3">
          <span> | </span>
          <a href="javascript:;" :style="{ opacity: showType === 'skill' ? '1' : '0.5' }" @click="handleChangeItemType('skill')">技能</a>
        </template>
        <span> | </span>
        <a href="javascript:;" :style="{ opacity: showType === 'rune' ? '1' : '0.5' }" @click="handleChangeItemType('rune')">符石</a>
        <span> | </span>
        <a href="javascript:;" :style="{ opacity: showType === 'relic' ? '1' : '0.5' }" @click="handleChangeItemType('relic')">圣物</a>
      </h4>
      <div class="item-list-item" v-show="showType === 'equip'">
        <div class="action">
          <span style="margin-right: 10px; font-size: 12px">
            容量：
            <span :style="{ color: userStore.getItemEquipTotal >= userStore.getItemCapacity ? 'red' : '' }">{{ itemListTotal }}</span>
            / {{ userStore.getItemCapacity }}
          </span>
          <el-button size="small" type="success" plain @click="handleArrange">整理</el-button>
          <el-popover class="affix" popper-class="item-filter-wrap" placement="top" width="100%" trigger="click" :teleported="false">
            <template #reference>
              <el-button size="small" type="primary" plain>筛选</el-button>
            </template>
            <p>装备类型</p>
            <el-button
              size="small"
              type="primary"
              plain
              class="item"
              v-for="item of equipTypeList.slice(0, 5)"
              :key="item"
              @click="handleAddFilterType(item)"
              :style="{ borderColor: filterType.includes(item) ? 'red' : '' }"
            >
              {{ equipIcon[item] }}
            </el-button>
            <el-button
              size="small"
              type="primary"
              plain
              class="item"
              v-for="item of equipTypeList.slice(5, 10)"
              :key="item"
              @click="handleAddFilterType(item)"
              :style="{ borderColor: filterType.includes(item) ? 'red' : '' }"
            >
              {{ equipIcon[item] }}
            </el-button>
            <p>装备品质</p>
            <div>
              <el-button
                size="small"
                type="primary"
                plain
                class="item"
                v-for="item of equipQualityList.slice(0, 5)"
                :key="item"
                @click="handleAddFilterQuality(item)"
                :style="{ borderColor: filterQuality.includes(item) ? 'red' : '' }"
              >
                <span :class="item">{{ equipQualityText[item] }}</span>
              </el-button>
            </div>
            <div>
              <el-button
                size="small"
                type="primary"
                plain
                class="item"
                v-for="item of equipQualityList.slice(5)"
                :key="item"
                @click="handleAddFilterQuality(item)"
                :style="{ borderColor: filterQuality.includes(item) ? 'red' : '' }"
                style="margin-top: 10px"
              >
                <span :class="item">{{ equipQualityText[item] }}</span>
              </el-button>
            </div>
            <p>搜索名称</p>
            <el-input size="small" v-model="filterName"></el-input>
            <p>搜索词条</p>
            <el-input size="small" v-model="filterAffix"></el-input>
            <br />
            <br />
            <el-button size="small" type="success" plain @click="handleResetFilter">重置</el-button>
          </el-popover>
          <el-popover placement="top" width="150px" trigger="click" :teleported="false">
            <template #reference>
              <el-button size="small" type="warning" plain style="margin-left: 12px" @click="handleResetFilter">批量出售</el-button>
            </template>
            <div class="batch-sell affix">
              <el-button
                size="small"
                type="primary"
                plain
                v-for="item of batchSellList.slice(...getBatSellSliceArr)"
                :key="item.key"
                @click="handleBatchSell(item)"
              >
                <span :class="item.key">{{ item.name }}及以下</span>
              </el-button>
              <el-button size="small" type="primary" plain @click="handleSellAll">
                <span style="color: red; font-weight: bold">全部出售</span>
              </el-button>
            </div>
          </el-popover>
        </div>
        <div class="item-btn-wrap btn-wrap">
          <el-row :gutter="10">
            <el-col :span="12" v-for="item of itemList" :key="item.id">
              <CommonBtn
                size="small"
                @done="showItemDetail(item, true)"
                :style="{ position: 'relative', borderColor: equipInfo?.id === item.id ? 'red' : '' }"
              >
                <span v-html="getEquipName(item, item.type)"></span>
                <el-icon v-show="item.isLock" color="#67c23a" style="position: absolute; top: 0; left: 0; z-index: 1"><Lock /> </el-icon>
              </CommonBtn>
            </el-col>
          </el-row>
        </div>
      </div>
      <div class="item-list-item" v-show="showType === 'rune'">
        <div class="action">
          <span style="margin-right: 10px; font-size: 12px">
            容量：
            <span :style="{ color: userStore.getRuneCapacity < userStore.runeList.length ? 'red' : '' }">{{ userStore.runeList.length }}</span>
            / {{ userStore.getRuneCapacity }}
          </span>
          <el-button size="small" type="success" plain @click="handleArrangeRune">整理</el-button>
          <el-popover class="affix" popper-class="item-filter-wrap" placement="top" width="100%" trigger="click" :teleported="false">
            <template #reference>
              <el-button size="small" type="primary" plain>筛选</el-button>
            </template>
            <p>战魂所属</p>
            <el-button
              size="small"
              type="primary"
              plain
              class="item"
              v-for="item of [1, 2, 3]"
              :key="item"
              @click="handleAddFilterSoulType(item)"
              :style="{ borderColor: filterSoulType.includes(item) ? 'red' : '' }"
            >
              <span :class="soulClass[item]">{{ RuneRoleText[item] }}</span>
            </el-button>
            <p>搜索名称</p>
            <el-input size="small" v-model="filterRuneName"></el-input>
            <br />
            <br />
            <el-button size="small" type="success" plain @click="handleResetRuneFilter">重置</el-button>
          </el-popover>
          <el-popover placement="top" width="150px" trigger="click" :teleported="false">
            <template #reference>
              <el-button size="small" type="warning" plain style="margin-left: 12px" @click="handleResetFilter">批量出售</el-button>
            </template>
            <div class="batch-sell affix">
              <el-button size="small" type="primary" plain v-for="item of batchSellRuneList" :key="item.key" @click="handleBatchSellRune(item)">
                <span :class="item.key">{{ item.name }}及以下</span>
              </el-button>
            </div>
          </el-popover>
        </div>
        <div class="item-btn-wrap btn-wrap">
          <el-row :gutter="10">
            <el-col :span="12" v-for="item of runeItemList" :key="item.id">
              <CommonBtn
                size="small"
                :style="{ position: 'relative', borderColor: runeInfo?.id === item.id ? 'red' : '' }"
                @done="showRuneDetail(item, true)"
              >
                <span :class="item.quality" v-html="getRuneName(item.runeId)"></span>
                <el-icon v-show="item.isLock" color="#67c23a" style="position: absolute; top: 0; left: 0; z-index: 1"><Lock /> </el-icon>
              </CommonBtn>
            </el-col>
          </el-row>
        </div>
      </div>
      <div class="item-list-item" v-show="showType === 'relic'" v-if="userStore.advanceLevel === 4">
        <div class="action">
          <el-button size="small" type="success" plain @click="handleArrangeRelic">整理</el-button>
          <el-popover class="affix" popper-class="item-filter-wrap" placement="top" width="100%" trigger="click" :teleported="false">
            <template #reference>
              <el-button size="small" type="primary" plain>筛选</el-button>
            </template>
            <p>元素</p>
            <el-button
              size="small"
              type="primary"
              plain
              class="item"
              v-for="(val, key) of relicIcon"
              :key="key"
              @click="handleAddFilterElement(key)"
              :style="{ borderColor: filterElementType?.includes(key) ? 'red' : '' }"
            >
              <span>{{ val }}</span>
            </el-button>
            <p>搜索名称</p>
            <el-input size="small" v-model="filterRelicName"></el-input>
            <br />
            <br />
            <el-button size="small" type="success" plain @click="handleResetRelicFilter">重置</el-button>
          </el-popover>
        </div>
        <div class="item-btn-wrap btn-wrap">
          <el-row :gutter="10">
            <el-col :span="12" v-for="item of relicItemList" :key="item.id">
              <CommonBtn
                size="small"
                :style="{ position: 'relative', borderColor: relicInfo?.id === item.id ? 'red' : '' }"
                @done="showRelicDetail(item, true)"
              >
                <span :class="item.quality" v-html="getRelicName(item.relicId, item.count)"></span>
                <el-icon v-show="item.isLock" color="#67c23a" style="position: absolute; top: 0; left: 0; z-index: 1"><Lock /> </el-icon>
              </CommonBtn>
            </el-col>
          </el-row>
        </div>
      </div>
      <div class="item-list-item" v-show="showType === 'skill'" v-if="userStore.soulType > 3">
        <div class="action">
          <el-button size="small" type="success" plain @click="handleArrangeSkill">整理</el-button>
          <el-popover class="affix" popper-class="item-filter-wrap" placement="top" width="100%" trigger="click" :teleported="false">
            <template #reference>
              <el-button size="small" type="primary" plain>筛选</el-button>
            </template>
            <p>书型</p>
            <el-button
              size="small"
              type="primary"
              plain
              class="item"
              v-for="(val, key) of skillItemElementIcon"
              :key="key"
              @click="handleAddFilterSkillMainElement(key)"
              :style="{ borderColor: filterSkillMainElmentType?.includes(key) ? 'red' : '' }"
            >
              <span>{{ val }}</span>
            </el-button>
            <p>元素</p>
            <el-button
              size="small"
              type="primary"
              plain
              class="item"
              v-for="(val, key) of relicIcon"
              :key="key"
              @click="handleAddFilterSkillElement(key)"
              :style="{ borderColor: filterSkillElementType?.includes(key) ? 'red' : '' }"
            >
              <span>{{ val }}</span>
            </el-button>
            <p>搜索名称</p>
            <el-input size="small" v-model="filterSkillName"></el-input>
            <br />
            <br />
            <el-button size="small" type="success" plain @click="handleResetSkillFilter">重置</el-button>
          </el-popover>
        </div>
        <div class="item-btn-wrap btn-wrap">
          <el-row :gutter="10">
            <el-col :span="12" v-for="item of skillItemList" :key="item.id">
              <CommonBtn
                size="small"
                :style="{ position: 'relative', borderColor: skillInfo?.id === item.id ? 'red' : '' }"
                @done="showSkillDetail(item, true)"
              >
                <span :class="item.quality" v-html="getSkillName(item)"></span>
                <el-icon v-show="item.isLock" color="#67c23a" style="position: absolute; top: 0; left: 0; z-index: 1"><Lock /> </el-icon>
              </CommonBtn>
            </el-col>
          </el-row>
        </div>
      </div>
    </div>
    <el-dialog v-model="compareShow" title="装备比对" width="100%" style="max-width: 500px">
      <template v-if="equipInfo">
        <EquipCompare :equipInfo="equipInfo" @close="handleCompareClose" />
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="compareShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="runeCompareShow" title="符石比对" width="100%" style="max-width: 500px">
      <template v-if="runeInfo">
        <RuneCompare :runeInfo="runeInfo" :index="runeCompareIndex" @close="handleCompareRuneClose" />
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="runeCompareShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="relicCompareShow" title="圣物比对" width="100%" style="max-width: 500px">
      <template v-if="relicInfo">
        <RelicCompare :relicInfo="relicInfo" :index="relicCompareIndex" @close="handleCompareRelicClose" />
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="relicCompareShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>

    <el-dialog v-model="skillCompareShow" title="技能比对" width="100%" style="max-width: 500px">
      <template v-if="skillInfo">
        <SkillCompare :skillInfo="skillInfo" :index="skillCompareIndex" @close="handleCompareSkillClose" />
      </template>
      <template #footer>
        <p style="text-align: center">
          <el-button plain @click="skillCompareShow = false" size="small">关闭</el-button>
        </p>
      </template>
    </el-dialog>
    <FooterBar />
  </div>
</template>

<style lang="scss" scoped>
.item-page {
  padding-top: 20px;
  height: 100%;
  display: flex;
  flex-flow: column nowrap;
  p {
    font-size: 14px;
    margin: 4px 0;
  }
  .basic-info {
    display: flex;
    margin-bottom: 10px;
    height: 436px;
    max-height: 50vh;
    .user-info-wrap {
      width: calc(50% - 5px);
      display: flex;
      flex-flow: column nowrap;
      .equip-list {
        border: 2px var(--fontColor) solid;
        border-radius: 5px;
        margin-bottom: 10px;
        overflow: hidden;
        display: flex;
        flex-flow: column nowrap;
        .btn-wrap {
          overflow-y: auto;
          overflow-x: hidden;
          padding: 0 8px;
          margin-bottom: 0;
        }
      }
      .user-attr-btn-wrap {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
    .item-info-wrap {
      height: 100%;
      padding: 0 8px 10px 8px;
      width: calc(50% - 5px);
      border: 2px var(--fontColor) solid;
      border-radius: 5px;
      margin-left: 10px;
      display: flex;
      flex-flow: column nowrap;
      overflow: hidden;
      max-height: 436px;
    }
    .item-btn-wrap {
      display: block;
      & > div {
        margin-bottom: 10px;
      }
    }
  }
  .item-list {
    flex: 1;
    border: 2px var(--fontColor) solid;
    border-radius: 5px;
    overflow: hidden;
    display: flex;
    flex-flow: column nowrap;
    h4 {
      a {
        color: #fff;
        text-decoration: none;
      }
    }
    .item-list-item {
      display: flex;
      flex-flow: column nowrap;
      overflow: hidden;
      .btn-wrap {
        margin: 0;
      }
    }
    .action {
      padding: 0 5px;
      margin-bottom: 5px;
      span {
        text-align: center;
      }
      .batch-sell {
        display: flex;
        flex-flow: column nowrap;
        button {
          width: 100%;
          margin: 4px 0;
        }
      }
    }
    .btn-wrap {
      max-height: 100%;
      overflow: auto;
      & > div {
        justify-content: unset;
        margin-bottom: 0;
      }
      .common-btn-wrap {
        margin-bottom: 10px;
      }
    }
  }
  .item-filter-wrap,
  .item-btn-wrap {
    :deep() {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
  }
  :deep() {
    .item-filter-wrap {
      .el-button:focus,
      .el-button:hover {
        background-color: unset;
      }
    }
    .see-attr {
      max-width: 230px;
      .border-wrap {
        p {
          margin: 4px 0;
        }
      }
    }
  }
}
</style>
